import { Box, Typography, useTheme } from "@outschool/backpack";
import { creditsPerLearnerPerMeeting } from "@outschool/business-rules";
import { Activity } from "@outschool/gql-backend-generated";
import {
  Trans,
  useLocalPriceDisplay,
  useTranslation,
} from "@outschool/localization";
import React from "react";

import { useEsaUserWithoutPaidMembership } from "../../../hooks/useEsaUserWithoutPaidMembership";
import { FreeForMembersChip } from "../../Chips/FreeForMembersChip";
import { CoinIcon } from "../../subscriptions/CoinIcon";
import EstimatedValueLineItem from "../mainBody/desktopActivityCards/EstimatedValueLineItem";

export default function MobileActivityCredits({
  activity,
  displayFullPriceOption = true,
}: {
  activity: Pick<
    Activity,
    | "allows_recurring_payment"
    | "isClub"
    | "is_ongoing_weekly"
    | "duration_weeks"
    | "hasTeacherSchedule"
    | "price_cents"
    | "weekly_meetings"
    | "isFlexSchedule"
    | "size_max"
    | "isOneTime"
    | "isSelfPaced"
    | "priceCredits"
    | "perWeeklyCourseCredits"
    | "isIncludedWithMembership"
  >;
  displayFullPriceOption?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\subcomponents\\MobileActivityCredits",
  );

  const theme = useTheme();
  const { makePerMeetingPriceCreditsText } = useLocalPriceDisplay();
  const flexSx = { flexDirection: "row", gap: 4, alignItems: "end" };
  const { isEsaUserWithoutPaidMembership } = useEsaUserWithoutPaidMembership();

  if (activity.hasTeacherSchedule) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <CoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="h6">
              {{ perMeetingPriceCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            credits per session
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.isOneTime) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <CoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="h6">
              {{ perMeetingPriceCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            credits per class
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.isClub) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <CoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="h6">
              {{ perMonthPriceCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            credits monthly
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.is_ongoing_weekly && activity.weekly_meetings === 1) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <CoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="h6">
              {{ perMeetingPriceCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            credits weekly
          </Typography>
        </Box>
      </Trans>
    );
  } else if (
    activity.is_ongoing_weekly &&
    (activity.weekly_meetings || 0) > 1
  ) {
    return (
      <Trans t={t}>
        <Box flex sx={{ flexDirection: "column" }}>
          <Box flex sx={flexSx}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <CoinIcon
                fillColor={theme.palette.warning.main}
                strokeColor={theme.palette.warning[700]}
              />
              <Typography variant="h6">
                {{ weeklyPrice: activity.priceCredits }}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              {" "}
              credits per week
            </Typography>
          </Box>
          {displayFullPriceOption && (
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <CoinIcon
                fillColor={theme.palette.warning.main}
                strokeColor={theme.palette.warning[700]}
              />
              <Typography variant="body2">
                (
                {{
                  perMeetingPriceCredits:
                    makePerMeetingPriceCreditsText(activity),
                }}{" "}
                credits per class)
              </Typography>
            </Box>
          )}
        </Box>
      </Trans>
    );
  } else if (activity.isSelfPaced) {
    if (activity.isIncludedWithMembership && !isEsaUserWithoutPaidMembership) {
      return (
        <Box flex sx={{ flexDirection: "column", gap: 10 }}>
          <FreeForMembersChip />
          <Trans t={t}>
            <Box flex sx={flexSx}>
              <Box
                sx={{
                  display: "flex",
                  gap: 4,
                  alignItems: "center",
                  margin: "auto",
                }}
              >
                <CoinIcon
                  fillColor={theme.palette.warning.main}
                  strokeColor={theme.palette.warning[700]}
                />
                <Typography variant="h6">0 </Typography>
                <Typography variant="body2">credits </Typography>
                <EstimatedValueLineItem
                  activity={activity}
                  sx={{ marginLeft: 5 }}
                />
              </Box>
            </Box>
          </Trans>
        </Box>
      );
    }
    if (activity.duration_weeks === 1) {
      return (
        <Trans t={t}>
          <Box flex sx={{ flexDirection: "column" }}>
            <Box flex sx={flexSx}>
              <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
                <CoinIcon
                  fillColor={theme.palette.warning.main}
                  strokeColor={theme.palette.warning[700]}
                />
                <Typography variant="h6">
                  {{ perCourseCredits: activity.priceCredits }}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ marginBottom: 1 }}>
                {" "}
                credits for all content
              </Typography>
            </Box>
          </Box>
        </Trans>
      );
    }
    return (
      <Trans t={t}>
        <Box flex sx={{ flexDirection: "column" }}>
          <Box flex sx={flexSx}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <CoinIcon
                fillColor={theme.palette.warning.main}
                strokeColor={theme.palette.warning[700]}
              />
              <Typography variant="h6">
                {{
                  perWeeklyCourseCredits: !!activity.weekly_meetings
                    ? creditsPerLearnerPerMeeting(activity) *
                      activity.weekly_meetings
                    : creditsPerLearnerPerMeeting(activity),
                }}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              {" "}
              credits weekly
            </Typography>
          </Box>
          {displayFullPriceOption && (
            <Box>
              <Typography variant="body2">or </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {{ perCourseCredits: activity.priceCredits }}
              </Typography>
              <Typography variant="body2"> for all content</Typography>
            </Box>
          )}
        </Box>
      </Trans>
    );
  } else if (!activity.allows_recurring_payment) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <CoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="h6">
              {{ perCourseCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            credits for{" "}
            {{
              classes:
                (activity.duration_weeks || 0) *
                (activity.weekly_meetings || 0),
            }}{" "}
            classes
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.allows_recurring_payment) {
    return (
      <Trans t={t}>
        <Box flex sx={{ flexDirection: "column" }}>
          <Box flex sx={flexSx}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <CoinIcon
                fillColor={theme.palette.warning.main}
                strokeColor={theme.palette.warning[700]}
              />
              <Typography variant="h6">
                {{
                  perWeeklyCourseCredits: !!activity.weekly_meetings
                    ? creditsPerLearnerPerMeeting(activity) *
                      activity.weekly_meetings
                    : creditsPerLearnerPerMeeting(activity),
                }}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              {" "}
              credits weekly
            </Typography>
          </Box>
          {displayFullPriceOption && (
            <Box sx={{ color: theme.palette.text.secondary }}>
              <Typography variant="body2">
                or {{ perCourseCredits: activity.priceCredits }} credits for{" "}
                {{
                  classes:
                    (activity.duration_weeks || 0) *
                    (activity.weekly_meetings || 0),
                }}{" "}
                classes
              </Typography>
            </Box>
          )}
        </Box>
      </Trans>
    );
  } else {
    return null;
  }
}
